import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { tLang, getInitialLang, setGlobalLang } from "../i18n.js";
import LangSelect from "../components/front/LangSelect.jsx";
import VideoIntro from "../components/video/VideoIntro.jsx";

const INTRO_STORAGE_KEY = "bestPlaceDemoIntroSeen";

const VIDEO_LESSONS_BY_LANG = {
  de: [
    {
      id: "overview",
      title: "Video 1",
      subtitle: "Service-Überblick",
      accentWord: "Service",
      description:
        "Ein kurzer Einstieg: Was Best Place für Restaurants macht und wie der Service funktioniert.",
      src: "/img/video-lessons/start.mp4",
      poster: "/img/video-lessons/poster1.webp",
    },
    {
      id: "qr-menu",
      title: "Video 2",
      subtitle: "QR-Menü erstellen",
      accentWord: "QR",
      description:
        "So erstellen Sie schnell ein digitales Menü und erhalten einen QR-Code für Ihre Gäste.",
      src: "/img/video-lessons/start-en.mp4",
      poster: "/img/video-lessons/poster2.webp",
    },
    {
      id: "photos-editing",
      title: "Video 3",
      subtitle: "Fotos & Bearbeitung",
      accentWord: "Fotos",
      description:
        "So fügen Sie Fotos hinzu, ändern Preise und bearbeiten Ihr Restaurant-Menü.",
      src: "/img/video-lessons/photos-editing.mp4",
      poster: "/img/video-lessons/poster3.webp",
    },
  ],

  en: [
    {
      id: "overview",
      title: "Video 1",
      subtitle: "Service overview",
      accentWord: "Service",
      description:
        "A short introduction: what Best Place does for restaurants and how the service works.",
      src: "/img/video-lessons/start.mp4",
      poster: "/img/video-lessons/poster1.webp",
    },
    {
      id: "qr-menu",
      title: "Video 2",
      subtitle: "Create QR menu",
      accentWord: "QR",
      description:
        "Learn how to quickly create a digital menu and get a QR code for your guests.",
      src: "/img/video-lessons/start-en.mp4",
      poster: "/img/video-lessons/poster2.webp",
    },
    {
      id: "photos-editing",
      title: "Video 3",
      subtitle: "Photos & editing",
      accentWord: "Photos",
      description:
        "Add photos, update prices and edit your restaurant menu content.",
      src: "/img/video-lessons/photos-editing-en.mp4",
      poster: "/img/video-lessons/poster3.webp",
    },
  ],

  
  no: [
    {
      id: "overview",
      title: "Video 1",
      subtitle: "Oversikt over tjenesten",
      accentWord: "tjenesten",
      description:
        "En kort introduksjon til hva Best Place tilbyr restauranter, og hvordan tjenesten fungerer.",
      src: "/img/video-lessons/start.mp4",
      poster: "/img/video-lessons/poster1.webp",
    },
    {
      id: "qr-menu",
      title: "Video 2",
      subtitle: "Opprett QR-meny",
      accentWord: "QR",
      description:
        "Lær hvordan du raskt oppretter en digital meny og får en QR-kode til gjestene dine.",
      src: "/img/video-lessons/start-en.mp4",
      poster: "/img/video-lessons/poster2.webp",
    },
    {
      id: "photos-editing",
      title: "Video 3",
      subtitle: "Bilder og redigering",
      accentWord: "Bilder",
      description:
        "Se hvordan du legger til bilder, oppdaterer priser og redigerer restaurantmenyen.",
      src: "/img/video-lessons/photos-editing.mp4",
      poster: "/img/video-lessons/poster3.webp",
    },
  ],

};

const INTRO_STEPS_BY_LANG = {
  de: [
    {
      number: 1,
      title: "Service-Überblick",
      text: "Ein kurzer Einstieg in die wichtigsten Möglichkeiten.",
    },
    {
      number: 2,
      title: "QR-Menü erstellen",
      text: "So erstellen Sie schnell ein digitales Menü mit QR-Code.",
    },
    {
      number: 3,
      title: "Fotos & Bearbeitung",
      text: "Bilder hinzufügen, Preise ändern und Inhalte verwalten.",
    },
  ],

  en: [
    {
      number: 1,
      title: "Service overview",
      text: "A short introduction to the main features.",
    },
    {
      number: 2,
      title: "Create QR menu",
      text: "Learn how to quickly create a digital menu with a QR code.",
    },
    {
      number: 3,
      title: "Photos & editing",
      text: "Add images, update prices, and manage your content.",
    },
  ],

  
  no: [
    {
      number: 1,
      title: "Oversikt over tjenesten",
      text: "En kort introduksjon til de viktigste funksjonene.",
    },
    {
      number: 2,
      title: "Opprett QR-meny",
      text: "Lær hvordan du raskt lager en digital meny med QR-kode.",
    },
    {
      number: 3,
      title: "Bilder og redigering",
      text: "Legg til bilder, endre priser og administrer innholdet.",
    },
  ],

};


function renderTitleWithAccent(text, accentWord) {
  const safeText = String(text || "");
  const safeAccent = String(accentWord || "").trim();

  if (!safeText || !safeAccent || !safeText.includes(safeAccent)) {
    return safeText;
  }

  const [before, after] = safeText.split(safeAccent);

  return (
    <>
      {before}
      <span className="video__title-accent">{safeAccent}</span>
      {after}
    </>
  );
}



const DEMO_LANGS = ["de", "en", "no"];

function normalizeDemoLang(value) {
  const v = String(value || "").trim().toLowerCase();
  return DEMO_LANGS.includes(v) ? v : "de";
}

function readLang() {
  try {
    const v = localStorage.getItem("lang");
    return DEMO_LANGS.includes(v) ? v : null;
  } catch {
    return null;
  }
}

const DEMO_NO_FALLBACKS = {
  "demo.slider.prev": "Forrige",
  "demo.slider.next": "Neste",
  "demo.slider.goTo": "Gå til video",
  "demo.video.unsupported": "Nettleseren din støtter ikke video.",
  "demo.video.pause": "Sett videoen på pause",
  "demo.video.play": "Spill av video",
  "landing.try.now": "Prøv gratis",
};

export default function DemoPage({ forcedLang = null }) {
  const navigate = useNavigate();

  const routeLang = normalizeDemoLang(forcedLang);

  
  const [lang, setLang] = useState(() => {
    if (DEMO_LANGS.includes(forcedLang)) return routeLang;
    return readLang() || normalizeDemoLang(getInitialLang());
  });


  const [open, setOpen] = useState(false);

  const [showIntro, setShowIntro] = useState(() => {
    if (typeof window === "undefined") return true;

    return sessionStorage.getItem(INTRO_STORAGE_KEY) !== "1";
  });

  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isVideoButtonVisible, setIsVideoButtonVisible] = useState(true);

  const closingRef = useRef(false);
  const activeVideoRef = useRef(null);
  const videoButtonTimerRef = useRef(null);
  const lastRevealAtRef = useRef(0);

  useEffect(() => {
  return () => {
    if (videoButtonTimerRef.current) {
      window.clearTimeout(videoButtonTimerRef.current);
      videoButtonTimerRef.current = null;
    }
  };
}, []);
 
  useEffect(() => {
    if (!DEMO_LANGS.includes(forcedLang)) return;

    setLang(routeLang);
    setGlobalLang(routeLang);

    try {
      localStorage.setItem("lang", routeLang);
    } catch {}
  }, [forcedLang, routeLang]);


  useEffect(() => {
    setGlobalLang(lang);
  }, [lang]);

  useEffect(() => {
    setActiveVideoIndex(0);
  }, [lang]);

  useEffect(() => {
  setIsVideoPlaying(false);
  setIsVideoButtonVisible(true);

  if (videoButtonTimerRef.current) {
    window.clearTimeout(videoButtonTimerRef.current);
    videoButtonTimerRef.current = null;
  }
}, [activeVideoIndex, lang]);

  const ta = useMemo(() => {
  return (key, fallbacks = {}) => {
    const fb =
      lang === "no"
        ? DEMO_NO_FALLBACKS[key] ?? fallbacks.en ?? fallbacks.de
        : lang === "en"
          ? fallbacks.en
          : fallbacks.de;

    return tLang(key, lang, fb);
  };
}, [lang]);

  const handleLangChange = (nextLang) => {
    const v = normalizeDemoLang(nextLang);

    setGlobalLang(v);
    setLang(v);

    try {
      localStorage.setItem("lang", v);
    } catch {}

    navigate(`/${v}/demo`);
  };

  useEffect(() => {
    const t = setTimeout(() => setOpen(true), 20);
    return () => clearTimeout(t);
  }, []);

  const ANIM_MS = 360;

  const closeTo = (to) => {
    if (closingRef.current) return;
    closingRef.current = true;

    setOpen(false);

    setTimeout(() => {
      if (to === "BACK") {
        navigate(`/${lang}/`, { replace: true });
      } else {
        navigate(to);
      }

      closingRef.current = false;
    }, ANIM_MS);
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        closeTo("BACK");
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const videoItems = VIDEO_LESSONS_BY_LANG[lang] || VIDEO_LESSONS_BY_LANG.de;
  const introSteps = INTRO_STEPS_BY_LANG[lang] || INTRO_STEPS_BY_LANG.de;
  const activeVideo = videoItems[activeVideoIndex] || videoItems[0];

  const registerPath = `/${lang}/register`;

  const closeIntro = () => {
    try {
      sessionStorage.setItem(INTRO_STORAGE_KEY, "1");
    } catch {}

    setShowIntro(false);
  };

  const clearVideoButtonTimer = () => {
  if (!videoButtonTimerRef.current) return;

  window.clearTimeout(videoButtonTimerRef.current);
  videoButtonTimerRef.current = null;
};

const hideVideoButtonSoon = (delay = 900) => {
  clearVideoButtonTimer();

  videoButtonTimerRef.current = window.setTimeout(() => {
    setIsVideoButtonVisible(false);
  }, delay);
};

const revealVideoButton = () => {
  setIsVideoButtonVisible(true);

  const video = activeVideoRef.current;

  if (video && !video.paused && !video.ended) {
    hideVideoButtonSoon(1600);
  }
};

  const pauseActiveVideo = () => {
  if (!activeVideoRef.current) return;

  clearVideoButtonTimer();

  activeVideoRef.current.pause();
  setIsVideoPlaying(false);
  setIsVideoButtonVisible(true);
};

const toggleActiveVideo = async () => {
  const video = activeVideoRef.current;
  if (!video) return;

  if (video.paused || video.ended) {
    try {
      await video.play();
      setIsVideoPlaying(true);
      hideVideoButtonSoon(700);
    } catch (err) {
      console.error("Video play failed:", err);
    }

    return;
  }

  clearVideoButtonTimer();

  video.pause();
  setIsVideoPlaying(false);
  setIsVideoButtonVisible(true);
};

  const goToVideo = (nextIndex) => {
    const total = videoItems.length;
    if (!total) return;

    pauseActiveVideo();

    const safeIndex = ((nextIndex % total) + total) % total;
    setActiveVideoIndex(safeIndex);
  };

  return (
    <div className={`legalPage demoPage ${open ? "legalPage--open" : ""}`}>
      <header className="legalPage__top">
        <div className="legalPage__topInner">
          <div className="legalPage__topInner--logo">
            <div
              className="legalPage__back"
              role="button"
              tabIndex={0}
              onClick={() => closeTo("BACK")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") closeTo("BACK");
              }}
            >
            </div>
              <Link
                to={`/${lang}/`}
                className="logo-wrapper-land logo-wrapper-land--legal"
                aria-label={
                  lang === "de"
                    ? "Zur Startseite"
                    : lang === "no"
                      ? "Til forsiden"
                      : "Go to homepage"
                }
            />
            </div>   
          
          <LangSelect
            value={lang}
            onChange={handleLangChange}
            availableLangs={DEMO_LANGS}
          />

        </div>
      </header>

      {showIntro ? (
        <VideoIntro lang={lang} steps={introSteps} onFinish={closeIntro} />
      ) : (
        <main className="video__main">
          <div className="title__block">
            <h1 className="video__title video__title--dynamic">
              <span className="video__title-video-name">
                {activeVideo?.title}:{" "}
              </span>

              {renderTitleWithAccent(activeVideo?.subtitle, activeVideo?.accentWord)}
            </h1>
          </div>

          <section className="legalPage__card demoPage__card">
            <div className="demoPage__slider-head">

              <button
                type="button"
                className="demoPage__slider-btn"
                onClick={() => goToVideo(activeVideoIndex - 1)}
              >
                {ta("demo.slider.prev", {
                  en: "Previous",
                  de: "Zurück",
                })}
              </button>

              <div className="demoPage__slider-count">
                {String(activeVideoIndex + 1).padStart(2, "0")} /{" "}
                {String(videoItems.length).padStart(2, "0")}
              </div>

              <button
                type="button"
                className="demoPage__slider-btn"
                onClick={() => goToVideo(activeVideoIndex + 1)}
              >
                {ta("demo.slider.next", {
                  en: "Next",
                  de: "Weiter",
                })}
              </button>
            </div>

            <div className="demoPage__slider">
              <article className="demoPage__slide" key={`${lang}-${activeVideo?.id}`}>
                <div
                  className="demoPage__video"
                  onMouseMove={revealVideoButton}
                  onTouchStart={revealVideoButton}
                >
                  <video
                    ref={activeVideoRef}
                    key={`${lang}-${activeVideo?.id}-${activeVideo?.src}`}
                    controls
                    playsInline
                    preload="metadata"
                    poster={activeVideo?.poster}
                    width="100%"
                    onPlay={() => {
                      setIsVideoPlaying(true);
                      hideVideoButtonSoon(700);
                    }}
                    onPause={() => {
                      clearVideoButtonTimer();
                      setIsVideoPlaying(false);
                      setIsVideoButtonVisible(true);
                    }}
                    onEnded={() => {
                      clearVideoButtonTimer();
                      setIsVideoPlaying(false);
                      setIsVideoButtonVisible(true);
                    }}
                  >
                    <source src={activeVideo?.src} type="video/mp4" />

                    {ta("demo.video.unsupported", {
                      en: "Your browser does not support video.",
                      de: "Ihr Browser unterstützt kein Video.",
                    })}
                  </video>

                  <button
                    type="button"
                    className={`demoPage__video-toggle ${
                      isVideoPlaying ? "is-playing" : ""
                    } ${
                      !isVideoPlaying || isVideoButtonVisible ? "is-visible" : "is-hidden"
                    }`}
                    onClick={toggleActiveVideo}
                    aria-label={
                      isVideoPlaying
                        ? ta("demo.video.pause", {
                            en: "Pause video",
                            de: "Video pausieren",
                          })
                        : ta("demo.video.play", {
                            en: "Play video",
                            de: "Video starten",
                          })
                    }
                  >
                    {isVideoPlaying ? (
                      <span className="demoPage__video-pause" aria-hidden="true">
                        <span />
                        <span />
                      </span>
                    ) : (
                      <span className="demoPage__video-play" aria-hidden="true" />
                    )}
                  </button>
                </div>

                <div className="demoPage__slide-info">
                  <div className="demoPage__slider-dots">
                    {videoItems.map((video, index) => (
                      <button
                        key={video.id}
                        type="button"
                        className={`demoPage__slider-dot ${
                          index === activeVideoIndex ? "is-active" : ""
                        }`}
                        aria-label={`${ta("demo.slider.goTo", {
                          en: "Go to video",
                          de: "Zum Video",
                        })} ${index + 1}`}
                        onClick={() => goToVideo(index)}
                      />
                    ))}
                  </div>

                  <h3>{activeVideo?.subtitle}</h3>

                  <p>{activeVideo?.description}</p>
                </div>
              </article>
            </div>

            <div className="services__button-container">
              <Link
                to={registerPath}
                className="services__button-register services__button-register--top"
              >
                {ta("landing.try.now", {
                  en: "Try it for free",
                  de: "Probieren Sie es kostenlos aus",
                })}
              </Link>
            </div>
          </section>
        </main>
      )}
    </div>
  );
}